import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Heading, Text } from '@/Components/ui/typography';
import { Separator } from '@/Components/ui/separator';
import { Stack } from '@/Components/ui/layout';
import { Grid } from '@/Components/ui/layout';
import { Container } from '@/Components/ui/container';
import { ArrowLeft, Edit, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { getColor } from '@/config/theme';
import { PostJobForm } from '@/HireWithUs/Pages/PostJob/PostJobForm';
import {
  formatSalaryDisplay,
  validateSalaryRangeInput,
} from '@/CompanyAdmin/utils/salaryUtils';
import { useCompanyAccess } from '@/HireWithUs/Context/CompanyAccessContext';

/**
 * UI-only redesign:
 * - Keeps your original data fields & PUT /update_jd endpoint
 * - Clean header, meta row, section cards, chips, bulletized lists
 * - Edit drawer preserved (same fields & handlers)
 * - No “Apply Now” button (per your request)
 */

function JobDescriptionView({ job, onClose, onSave }) {
  const { can } = useCompanyAccess();
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    jobTitle: "",
    jobType: "",
    companyLocation: "",
    minExperience: "",
    maxExperience: "",
    employmentType: "",
    salaryRange: "",
    preferredQualification: "",
    jobSummary: "",
    keyResponsibilities: "",
    requiredSkills: "",
    optionalSkills: "",
    softSkills: "",
    workHours: "",
    companyCulture: "",
    travelRequirement: "",
    hiringProcess: "",
    applicationDeadline: "",
    mandatoryProfile: false,
    preferrenceOne: "",
    preferrenceTwo: "",
    preferrenceThree: "",
    criticalSkills: [],
    skillPenalties: {},
  });

  // Hydrate from `job` (supports both snake_case & camelCase from your backend)
  useEffect(() => {
    if (!job) return;
    
    // Helper function to ensure string values
    const ensureString = (value) => {
      if (typeof value === 'string') return value;
      if (Array.isArray(value)) return value.join(', ');
      if (typeof value === 'object' && value !== null) return JSON.stringify(value);
      return String(value || '');
    };

    // Parse experience range into min/max if needed
    const parseExperience = (expRange) => {
      if (!expRange) return { min: '', max: '' };
      if (typeof expRange === 'string' && expRange.includes('-')) {
        const parts = expRange.split('-');
        return {
          min: parts[0]?.trim() || '',
          max: parts[1]?.trim() || ''
        };
      }
      return { min: expRange, max: '' };
    };

    const experience = parseExperience(job.experienceRange || job.experience_range || job.minExperience || job.min_experience);

    const requiredSkillsStr = ensureString(job.requiredSkills || job.required_skills || job.required_qualifications || "");
    const optionalSkillsStr = ensureString(job.optionalSkills || job.optional_skills || job.good_to_have_skills || "");
    const softSkillsStr = ensureString(job.softSkills || job.soft_skills || "");

    const newFormData = {
      jobTitle: job.jobTitle || job.job_title || "",
      jobType: job.jobType || job.job_type || "",
      companyLocation: job.companyLocation || job.location || "",
      minExperience: job.minExperience || job.min_experience || job.min_exp || experience.min,
      maxExperience: job.maxExperience || job.max_experience || job.max_exp || experience.max,
      employmentType: job.employmentType || job.employment_type || "",
      salaryRange: (() => {
        const raw = job.salaryRange || job.salary_range || "";
        if (!raw) return "";
        const errs = validateSalaryRangeInput(raw);
        return errs.salaryRange ? "" : String(raw);
      })(),
      preferredQualification: (() => {
        // Handle preferred_qualifications which might be array or string
        const prefQual = job.preferredQualification || job.preferred_qualification || job.preferred_qualifications_raw || job.preferred_qualifications || "";
        if (Array.isArray(prefQual)) {
          return prefQual.join(', ');
        }
        return ensureString(prefQual);
      })(),
      jobSummary: ensureString(job.jobSummary || job.job_summary || job.job_overview || ""),
      keyResponsibilities: ensureString(job.keyResponsibilities || job.key_responsibilities || ""),
      requiredSkills: requiredSkillsStr,
      optionalSkills: optionalSkillsStr,
      softSkills: softSkillsStr,
      workHours: job.workHours || job.work_hours || "",
      companyCulture: ensureString(job.companyCulture || job.company_culture || job.company_overview || ""),
      travelRequirement: job.travelRequirement || job.travel_requirement || job.travel_requires || "",
      hiringProcess: ensureString(job.hiringProcess || job.hiring_process || job.how_to_apply || ""),
      applicationDeadline: job.applicationDeadline || job.application_deadline || "",
      mandatoryProfile: job.mandatoryProfile || job.mandatory_profile || false,
      preferrenceOne: job.preferrenceOne || job.preferrence_one || "",
      preferrenceTwo: job.preferrenceTwo || job.preferrence_two || "",
      preferrenceThree: job.preferrenceThree || job.preferrence_three || "",
      criticalSkills: (() => {
        const skills = job.criticalSkills || job.critical_skills;
        if (Array.isArray(skills)) return skills;
        if (typeof skills === 'string') {
          // Handle JSON array strings like ["React", "TypeScript", "JavaScript", "HTML/CSS"]
          if (skills.startsWith('[') && skills.endsWith(']')) {
            try {
              const parsed = JSON.parse(skills);
              if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
            } catch (e) {
              // If JSON parsing fails, fall through to regular processing
            }
          }
          // Handle comma-separated strings
          return skills.split(',').map(s => s.trim().replace(/["\[\]]/g, '')).filter(Boolean);
        }
        return [];
      })(),
      skillPenalties: typeof job.skillPenalties === 'object' && job.skillPenalties !== null ? job.skillPenalties : 
                     (job.skill_penalties ? (typeof job.skill_penalties === 'object' ? job.skill_penalties : {}) : {}),
      ...job,
    };

    setFormData(newFormData);
  }, [job]);

  // Helpers
  const splitList = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean).map(String);
    
    const str = String(val);
    
    // Handle JSON array strings like ["React", "TypeScript", "JavaScript", "HTML/CSS"]
    if (str.startsWith('[') && str.endsWith(']')) {
      try {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed)) {
          return parsed.filter(Boolean).map(String);
        }
      } catch (e) {
        // If JSON parsing fails, fall through to regular processing
      }
    }
    
    return str
      .replace(/•/g, "") // clean existing bullets
      .replace(/["\[\]]/g, "") // remove quotes and brackets
      .split(/[\n,]+/g)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const bullets = useMemo(
    () => ({
      responsibilities: splitList(formData.keyResponsibilities),
      required: splitList(formData.requiredSkills),
      optional: splitList(formData.optionalSkills),
      soft: splitList(formData.softSkills),
      prefs: [
        formData.preferrenceOne,
        formData.preferrenceTwo,
        formData.preferrenceThree,
      ].filter(Boolean),
    }),
    [formData]
  );

  // Parse skills into arrays for ReviewStep-like layout
  const requiredSkillTags = useMemo(() => splitList(formData.requiredSkills), [formData.requiredSkills]);
  const optionalSkillTags = useMemo(() => splitList(formData.optionalSkills), [formData.optionalSkills]);
  const softSkillTags = useMemo(() => splitList(formData.softSkills), [formData.softSkills]);

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const formatText = (text) => {
    if (!text) return 'Not specified';
    const stripped = stripHtml(text);
    return stripped || 'Not specified';
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  };

  // Format experience range for display
  const formatExperienceRange = () => {
    if (formData.minExperience && formData.maxExperience) {
      return `${formData.minExperience}-${formData.maxExperience} years`;
    } else if (formData.minExperience) {
      return `${formData.minExperience}+ years`;
    } else if (formData.maxExperience) {
      return `Up to ${formData.maxExperience} years`;
    }
    return "—";
  };

  // Escape HTML for safe insertion into PDF template
  const escapeHtml = (text) => {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Convert HTML to plain text for PDF (preserves line breaks)
  const htmlToText = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // Replace <br> and <p> tags with line breaks
    const text = tmp.innerText || tmp.textContent || '';
    return text.replace(/\n\n+/g, '\n\n'); // Normalize multiple line breaks
  };

  // PDF Export functionality
  const exportToPDF = () => {
    const salaryForPdf = formatSalaryDisplay(formData.salaryRange);
    // Check if formData has at least a job title
    if (!formData.jobTitle) {
      toast.error('Job description data is not available');
      return;
    }

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to export PDF');
      return;
    }
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Job Description - ${formData.jobTitle}</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              line-height: 1.6; 
              color: #333; 
              max-width: 800px; 
              margin: 0 auto; 
              padding: 20px;
              background: white;
            }
            .pdf-header { 
              text-align: center; 
              border-bottom: 3px solid #007bff; 
              padding-bottom: 20px; 
              margin-bottom: 30px; 
            }
            .pdf-title { 
              font-size: 28px; 
              font-weight: bold; 
              color: #007bff; 
              margin-bottom: 10px; 
            }
            .pdf-meta { 
              font-size: 16px; 
              color: #666; 
              margin-bottom: 5px; 
            }
            .pdf-section { 
              margin-bottom: 25px; 
              page-break-inside: avoid; 
            }
            .pdf-section-title { 
              font-size: 18px; 
              font-weight: bold; 
              color: #007bff; 
              margin-bottom: 10px; 
              border-bottom: 1px solid #e9ecef; 
              padding-bottom: 5px; 
            }
            .pdf-content { 
              margin-bottom: 15px; 
            }
            .pdf-skills { 
              display: flex; 
              flex-wrap: wrap; 
              gap: 8px; 
              margin-top: 10px; 
            }
            .pdf-skill-tag { 
              background: #f8f9fa; 
              border: 1px solid #dee2e6; 
              padding: 4px 8px; 
              border-radius: 4px; 
              font-size: 14px; 
            }
            .pdf-skill-tag.critical { 
              background: #fff3cd; 
              border-color: #ffeaa7; 
              font-weight: bold; 
            }
            .pdf-bullets { 
              margin: 10px 0; 
              padding-left: 20px; 
            }
            .pdf-bullets li { 
              margin-bottom: 5px; 
            }
            .pdf-grid { 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 20px; 
              margin-bottom: 20px; 
            }
            .pdf-kv { 
              margin-bottom: 10px; 
            }
            .pdf-kv-label { 
              font-weight: bold; 
              color: #666; 
              font-size: 14px; 
            }
            .pdf-kv-value { 
              margin-top: 2px; 
            }
            @media print {
              body { margin: 0; padding: 15px; }
              .pdf-section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="pdf-header">
            <div class="pdf-title">${escapeHtml(formData.jobTitle || "Job Position")}</div>
            <div class="pdf-meta">${escapeHtml(formData.jobType || "")} • ${escapeHtml(formData.companyLocation || "")} • ${escapeHtml(formatExperienceRange())}</div>
            ${salaryForPdf !== "Not specified" ? `<div class="pdf-meta">💰 ${escapeHtml(salaryForPdf)}</div>` : ''}
          </div>

          ${formData.jobSummary ? `
            <div class="pdf-section">
              <div class="pdf-section-title">About the Role</div>
              <div class="pdf-content">${htmlToText(formData.jobSummary).replace(/\n/g, '<br>')}</div>
            </div>
          ` : ''}

          ${formData.keyResponsibilities ? `
            <div class="pdf-section">
              <div class="pdf-section-title">Key Responsibilities</div>
              <div class="pdf-content">${htmlToText(formData.keyResponsibilities).replace(/\n/g, '<br>')}</div>
            </div>
          ` : ''}

          ${bullets.required.length > 0 ? `
            <div class="pdf-section">
              <div class="pdf-section-title">Required Skills</div>
              <div class="pdf-skills">
                ${bullets.required.map(skill => `<span class="pdf-skill-tag">${escapeHtml(skill)}</span>`).join('')}
              </div>
            </div>
          ` : ''}

          ${formData.criticalSkills && formData.criticalSkills.length > 0 ? `
            <div class="pdf-section">
              <div class="pdf-section-title">Critical Skills</div>
              <div class="pdf-skills">
                ${formData.criticalSkills.map(skill => `<span class="pdf-skill-tag critical">${escapeHtml(skill)}: ${formData.skillPenalties[skill] || 0}%</span>`).join('')}
              </div>
            </div>
          ` : ''}

          ${bullets.optional.length > 0 ? `
            <div class="pdf-section">
              <div class="pdf-section-title">Nice to Have</div>
              <div class="pdf-skills">
                ${bullets.optional.map(skill => `<span class="pdf-skill-tag">${escapeHtml(skill)}</span>`).join('')}
              </div>
            </div>
          ` : ''}

          ${bullets.soft.length > 0 ? `
            <div class="pdf-section">
              <div class="pdf-section-title">Soft Skills</div>
              <div class="pdf-skills">
                ${bullets.soft.map(skill => `<span class="pdf-skill-tag">${escapeHtml(skill)}</span>`).join('')}
              </div>
            </div>
          ` : ''}

          <div class="pdf-grid">
            ${formData.employmentType ? `
              <div class="pdf-kv">
                <div class="pdf-kv-label">Employment Type</div>
                <div class="pdf-kv-value">${escapeHtml(formData.employmentType)}</div>
              </div>
            ` : ''}
            ${formData.workHours ? `
              <div class="pdf-kv">
                <div class="pdf-kv-label">Work Hours</div>
                <div class="pdf-kv-value">${escapeHtml(formData.workHours)}</div>
              </div>
            ` : ''}
            ${formData.travelRequirement ? `
              <div class="pdf-kv">
                <div class="pdf-kv-label">Travel Requirement</div>
                <div class="pdf-kv-value">${escapeHtml(formData.travelRequirement)}</div>
              </div>
            ` : ''}
            ${formData.applicationDeadline ? `
              <div class="pdf-kv">
                <div class="pdf-kv-label">Application Deadline</div>
                <div class="pdf-kv-value">${escapeHtml(fmtDate(formData.applicationDeadline))}</div>
              </div>
            ` : ''}
          </div>

          ${formData.companyCulture ? `
            <div class="pdf-section">
              <div class="pdf-section-title">Company Culture</div>
              <div class="pdf-content">${htmlToText(formData.companyCulture).replace(/\n/g, '<br>')}</div>
            </div>
          ` : ''}

          ${formData.hiringProcess ? `
            <div class="pdf-section">
              <div class="pdf-section-title">Hiring Process</div>
              <div class="pdf-content">${htmlToText(formData.hiringProcess).replace(/\n/g, '<br>')}</div>
            </div>
          ` : ''}

          ${formData.preferredQualification ? `
            <div class="pdf-section">
              <div class="pdf-section-title">Preferred Qualifications</div>
              <div class="pdf-content">${htmlToText(formData.preferredQualification).replace(/\n/g, '<br>')}</div>
            </div>
          ` : ''}

          ${bullets.prefs.length > 0 ? `
            <div class="pdf-section">
              <div class="pdf-section-title">Preferences</div>
              <ul class="pdf-bullets">
                ${bullets.prefs.map(pref => `<li>${escapeHtml(pref)}</li>`).join('')}
              </ul>
            </div>
          ` : ''}

          <div style="margin-top: 40px; text-align: center; color: #666; font-size: 12px;">
            Generated on ${new Date().toLocaleDateString()} • NewZenAlpha Job Portal
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleWizardSuccess = (updatedJob) => {
    setIsEditing(false);
    if (updatedJob) {
      setFormData((prev) => ({ ...prev, ...updatedJob }));
    }
    onSave?.(updatedJob);
  };

  if (!job) return null;

  const requirementStatus = String(job.requirement_status || job.requirementStatus || '').trim().toLowerCase();
  const roleStatusLabel = ['', 'active', 'open'].includes(requirementStatus) ? 'Active' : 'Inactive';
  const roleStatusClass = roleStatusLabel === 'Inactive'
    ? 'border-slate-300 bg-slate-100 text-slate-700'
    : 'border-emerald-300 bg-emerald-50 text-emerald-800';

  return (
    <Container className="py-6 px-6 min-h-screen bg-muted/30">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          onClick={onClose}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Jobs
        </Button>
        <div className="flex gap-2">
          {!isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={exportToPDF}
              >
                <FileText className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
              {can('jobs.edit') && (
              <Button
                onClick={() => setIsEditing(true)}
                style={{
                  backgroundColor: getColor('primary'),
                  color: getColor('primaryForeground'),
                }}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Job Description
              </Button>
              )}
            </>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsEditing(false)}
              >
                <X className="w-4 h-4 mr-2" />
                Close Editor
              </Button>
            </div>
          )}
        </div>
      </div>

      {!isEditing ? (
        // ======== VIEW MODE (Using ReviewStep Layout) ========
        <div className="space-y-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-2">{formData.jobTitle || "Job Description"}</h2>
            <p className="text-muted-foreground">
              Review all the information below. Click "Edit Job Description" to make changes.
            </p>
          </div>

          {/* Basic Information */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Job Title</div>
                  <div className="text-base font-semibold">{formData.jobTitle || 'Not specified'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Job Type</div>
                  <div className="text-base">{formData.jobType || 'Not specified'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Location</div>
                  <div className="text-base">{formData.companyLocation || 'Not specified'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Experience</div>
                  <div className="text-base">
                    {formData.minExperience 
                      ? `${formData.minExperience}${formData.maxExperience ? ` - ${formData.maxExperience}` : '+'} years`
                      : 'Not specified'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Employment Type</div>
                  <div className="text-base">{formData.employmentType || 'Not specified'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Salary Range</div>
                  <div className="text-base">
                    {formatSalaryDisplay(formData.salaryRange)}
                  </div>
                </div>
                {formData.applicationDeadline && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Application Deadline</div>
                    <div className="text-base">{fmtDate(formData.applicationDeadline)}</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Job Details */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Job Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.jobSummary && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-2">Job Summary</div>
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: formData.jobSummary || '<p class="text-muted-foreground">Not specified</p>' }}
                  />
                </div>
              )}
              {formData.jobSummary && formData.keyResponsibilities && <Separator />}
              {formData.keyResponsibilities && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-2">Key Responsibilities</div>
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: formData.keyResponsibilities || '<p class="text-muted-foreground">Not specified</p>' }}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Skills & Qualifications */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Skills & Qualifications</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {requiredSkillTags.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-2">Required Skills</div>
                  <div className="flex flex-wrap gap-2">
                    {requiredSkillTags.map((skill, index) => (
                      <Badge
                        key={index}
                        variant="default"
                        className="flex items-center gap-1"
                        style={{
                          backgroundColor: getColor('primary'),
                          color: getColor('primaryForeground'),
                        }}
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {optionalSkillTags.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-2">Optional Skills</div>
                  <div className="flex flex-wrap gap-2">
                    {optionalSkillTags.map((skill, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {softSkillTags.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-2">Soft Skills</div>
                  <div className="flex flex-wrap gap-2">
                    {softSkillTags.map((skill, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {formData.criticalSkills && formData.criticalSkills.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-2">Critical Skills</div>
                  <div className="flex flex-wrap gap-2">
                    {formData.criticalSkills.map((skill, index) => (
                      <Badge
                        key={index}
                        variant="default"
                        className="flex items-center gap-1"
                        style={{
                          backgroundColor: getColor('primary'),
                          color: getColor('primaryForeground'),
                        }}
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {formData.preferredQualification && (
                <>
                  {(requiredSkillTags.length > 0 || optionalSkillTags.length > 0 || softSkillTags.length > 0 || (formData.criticalSkills && formData.criticalSkills.length > 0)) && <Separator />}
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-2">Preferred Qualifications</div>
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: formData.preferredQualification || '<p class="text-muted-foreground">Not specified</p>' }}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Company & Work Details */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Company & Work Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.workHours && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Work Hours</div>
                    <div className="text-base">{formData.workHours}</div>
                  </div>
                )}
                {formData.travelRequirement && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Travel Requirement</div>
                    <div className="text-base">{formData.travelRequirement}</div>
                  </div>
                )}
              </div>
              {formData.companyCulture && (
                <>
                  {(formData.workHours || formData.travelRequirement) && <Separator />}
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-2">Company Culture</div>
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: formData.companyCulture || '<p class="text-muted-foreground">Not specified</p>' }}
                    />
                  </div>
                </>
              )}
              {formData.hiringProcess && (
                <>
                  {(formData.workHours || formData.travelRequirement || formData.companyCulture) && <Separator />}
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-2">Hiring Process</div>
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: formData.hiringProcess || '<p class="text-muted-foreground">Not specified</p>' }}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Preferences */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {formData.preferrenceOne && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Preference 1</div>
                    <div className="text-base">{formData.preferrenceOne}</div>
                  </div>
                )}
                {formData.preferrenceTwo && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Preference 2</div>
                    <div className="text-base">{formData.preferrenceTwo}</div>
                  </div>
                )}
                {formData.preferrenceThree && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Preference 3</div>
                    <div className="text-base">{formData.preferrenceThree}</div>
                  </div>
                )}
              </div>
              <Separator />
              <div className="flex items-center space-x-2">
                <div className="text-sm font-medium text-muted-foreground">Mandatory Profile:</div>
                <div className="text-base">{formData.mandatoryProfile ? 'Yes' : 'No'}</div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Job Information */}
          {(job.created_at || job.status || job.requirement_status || job.requirementStatus || job.applicants !== undefined) && (
            <Card>
              <CardHeader>
                <CardTitle>Additional Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  {job.created_at && (
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-muted-foreground">Posted Date</div>
                      <div className="text-base">{fmtDate(job.created_at)}</div>
                    </div>
                  )}
                  <div className="flex flex-col">
                    <div className="text-sm font-medium text-muted-foreground">Role Status</div>
                    <Badge variant="outline" className={roleStatusClass}>{roleStatusLabel}</Badge>
                  </div>
                  <div className="flex flex-col">
                    <div className="text-sm font-medium text-muted-foreground">Role Opened</div>
                    <div className="text-base">{fmtDate(job.opened_at || job.requirement_date || job.created_at)}</div>
                  </div>
                  {roleStatusLabel === 'Inactive' && (
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-muted-foreground">Role Closed</div>
                      <div className="text-base">{job.closed_at ? fmtDate(job.closed_at) : '—'}</div>
                    </div>
                  )}
                  {job.applicants !== undefined && (
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-muted-foreground">Total Applications</div>
                      <div className="text-base">{job.applicants} applications received</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <PostJobForm
          mode="edit"
          embedded
          initialData={job}
          jobId={job?.job_id || job?.id}
          onCancel={() => setIsEditing(false)}
          onSuccess={handleWizardSuccess}
        />
      )}
    </Container>
  );
}

export default JobDescriptionView;
